import React, { useState, useEffect } from 'react';
import { Code2, Users, ChevronRight, Gamepad2, Dices, Play, Pause, RotateCcw, Sparkles, Recycle, Timer, BookOpen, Plus, X } from 'lucide-react';
import timerService from '../services/timerService';
import { getTeamMembers, addTeamMember, removeTeamMember } from '../services/teamService';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
}

interface PomodoroState {
  timeLeft: number;
  isRunning: boolean;
  mode: 'work' | 'shortBreak' | 'longBreak';
  cycles: number;
  settings: {
    workTime: number;
    shortBreakTime: number;
    longBreakTime: number;
    cyclesBeforeLongBreak: number;
  };
  stats: {
    completedPomodoros: number;
    completedShortBreaks: number;
    completedLongBreaks: number;
    totalWorkTime: number;
  };
}


const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  setIsOpen,
  activeSection,
  setActiveSection,
}) => {
  // Initialize pomodoro state from localStorage or use default values
  const [pomodoro, setPomodoro] = useState<PomodoroState>(() => {
    // Try to get saved pomodoro data from localStorage
    const savedPomodoro = localStorage.getItem('pomodoroState');
    if (savedPomodoro) {
      try {
        // Parse the saved JSON data
        const parsedData = JSON.parse(savedPomodoro);
        return parsedData;
      } catch (error) {
        console.error('Failed to parse saved pomodoro data:', error);
      }
    }
    
    // Return default state if no saved data exists or parsing failed
    return {
      timeLeft: 25 * 60,
      isRunning: false,
      mode: 'work',
      cycles: 0,
      settings: {
        workTime: 25 * 60,
        shortBreakTime: 5 * 60,
        longBreakTime: 15 * 60,
        cyclesBeforeLongBreak: 4
      },
      stats: {
        completedPomodoros: 0,
        completedShortBreaks: 0,
        completedLongBreaks: 0,
        totalWorkTime: 0
      }
    };
  });

  // Save pomodoro state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('pomodoroState', JSON.stringify(pomodoro));
  }, [pomodoro]);

  // Existing useEffect for pomodoro timer
  useEffect(() => {
    const handlePomodoroComplete = () => {
      let newMode: PomodoroState['mode'];
      let newTimeLeft: number;
      let newCycles = pomodoro.cycles;
      const newStats = { ...pomodoro.stats };

      if (pomodoro.mode === 'work') {
        newStats.completedPomodoros += 1;
        newCycles += 1;

        if (newCycles % pomodoro.settings.cyclesBeforeLongBreak === 0) {
          newMode = 'longBreak';
          newTimeLeft = pomodoro.settings.longBreakTime;
        } else {
          newMode = 'shortBreak';
          newTimeLeft = pomodoro.settings.shortBreakTime;
        }
      } else {
        if (pomodoro.mode === 'shortBreak') {
          newStats.completedShortBreaks += 1;
        } else {
          newStats.completedLongBreaks += 1;
        }
        newMode = 'work';
        newTimeLeft = pomodoro.settings.workTime;
      }

      if (Notification.permission === 'granted') {
        new Notification(
          `${pomodoro.mode === 'work' ? 'Time for a break!' : 'Back to work!'}`,
          {
            body: `${pomodoro.mode === 'work' 
              ? 'Great job! Take some time to rest.' 
              : 'Break is over. Let\'s get back to it!'}`,
            icon: '/favicon.ico'
          }
        );
      }

      setPomodoro(prev => ({
        ...prev,
        mode: newMode,
        timeLeft: newTimeLeft,
        cycles: newCycles,
        stats: newStats,
        isRunning: false
      }));
    };

    let timer: number;

    if (pomodoro.isRunning && pomodoro.timeLeft > 0) {
      timer = window.setInterval(() => {
        setPomodoro(prev => ({
          ...prev,
          timeLeft: prev.timeLeft - 1,
          stats: prev.mode === 'work' 
            ? { ...prev.stats, totalWorkTime: prev.stats.totalWorkTime + 1 }
            : prev.stats
        }));
      }, 1000);
    } else if (pomodoro.timeLeft === 0) {
      handlePomodoroComplete();
    }

    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [pomodoro.isRunning, pomodoro.timeLeft, pomodoro.mode, pomodoro.cycles, pomodoro.settings, pomodoro.stats]);

  // Team members state
  const [teamMembers, setTeamMembers] = useState<string[]>(() => getTeamMembers());
  const [newMemberName, setNewMemberName] = useState('');

  // Team member management functions
  const handleAddMember = () => {
    if (newMemberName.trim()) {
      addTeamMember(newMemberName.trim());
      setTeamMembers(getTeamMembers());
      setNewMemberName('');
    }
  };

  const handleRemoveMember = (memberName: string) => {
    removeTeamMember(memberName);
    setTeamMembers(getTeamMembers());
  };

  const togglePomodoro = () => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    setPomodoro(prev => ({
      ...prev,
      isRunning: !prev.isRunning
    }));
  };

  const resetPomodoro = () => {
    setPomodoro(prev => ({
      ...prev,
      timeLeft: prev.settings.workTime,
      isRunning: false,
      mode: 'work',
      cycles: 0
    }));
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    setIsOpen(false);
  };

  // Añadir estado para el timer de parejas
  const [pairTimer, setPairTimer] = useState(timerService.getTimerState());
  
  // Suscribirse al timerService
  useEffect(() => {
    const unsubscribe = timerService.subscribe((timerState) => {
      setPairTimer(timerState);
    });
    
    return unsubscribe;
  }, []);
  
  // Añadir el formato de tiempo para el contador
  const formatTimerCounter = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  return (
    <nav
      className={`
        fixed lg:static lg:block
        w-72 h-[calc(100vh-4rem)] 
        bg-white dark:bg-gray-800 
        shadow-lg lg:shadow-none
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        z-40
        flex flex-col
      `}
    >
      <div className="p-4 flex-1">
        <div className="space-y-2">
          <button
            onClick={() => handleSectionChange('dev')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'dev'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Code2 size={20} />
            <span>Developer Tools</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>

          <button
            onClick={() => handleSectionChange('agile')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'agile'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Recycle size={20} />
            <span>Agile Tools</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>

          <button
            onClick={() => handleSectionChange('xp')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'xp'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : pairTimer.isRunning
                    ? 'bg-red-500 dark:bg-red-900 text-white dark:text-red-200 hover:bg-red-600 dark:hover:bg-red-800'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Users size={20} />
            <span>Pair Programming</span>
            {pairTimer.isRunning && (
              <div className="ml-auto flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5 text-xs font-medium">
                <Timer size={12} />
                {formatTimerCounter(pairTimer.timeLeft)}
              </div>
            )}
            {!pairTimer.isRunning && (
              <ChevronRight size={16} className="ml-auto" />
            )}
          </button>

          <button
            onClick={() => handleSectionChange('teamname')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'teamname'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Sparkles size={20} />
            <span>Team Name Generator</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>

          <button
            onClick={() => handleSectionChange('ice')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'ice'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Gamepad2 size={20} />
            <span>Ice Breakers</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>

          <button
            onClick={() => handleSectionChange('roulette')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'roulette'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <Dices size={20} />
            <span>Teams Spinner</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>

          <button
            onClick={() => handleSectionChange('guides')}
            className={`
              w-full px-4 py-2 rounded-lg flex items-center space-x-3
              ${
                activeSection === 'guides'
                  ? 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'
              }
            `}
          >
            <BookOpen size={20} />
            <span>Guides</span>
            <ChevronRight size={16} className="ml-auto" />
          </button>
        </div>
      </div>

      <div className="p-4 border-t dark:border-gray-700">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
              <span className="text-red-600 dark:text-red-400">🍅</span>
              <span className="font-semibold">Pomodoro</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={togglePomodoro}
                className={`
                  p-2 rounded-lg transition-colors
                  ${pomodoro.isRunning
                    ? 'bg-red-500 dark:bg-red-900 text-white dark:text-red-200'
                    : 'bg-blue-500 dark:bg-blue-900 text-white dark:text-blue-200'
                  }
                `}
              >
                {pomodoro.isRunning ? <Pause size={18} /> : <Play size={18} />}
              </button>
              <button
                onClick={resetPomodoro}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200"
              >
                <RotateCcw size={18} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                {pomodoro.mode === 'work' 
                  ? 'Working' 
                  : pomodoro.mode === 'shortBreak'
                  ? 'Short Break'
                  : 'Long Break'
                }
              </span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Cycle: {pomodoro.cycles + 1}
            </span>
          </div>

          <div className={`text-2xl font-bold text-center ${
            pomodoro.mode === 'work'
              ? 'text-blue-600 dark:text-blue-400'
              : pomodoro.mode === 'shortBreak'
              ? 'text-green-600 dark:text-green-400'
              : 'text-purple-600 dark:text-purple-400'
          }`}>
            {formatTime(pomodoro.timeLeft)}
          </div>

          <div className="grid grid-cols-2 gap-2 text-center text-xs">
            <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
              <div className="text-gray-500 dark:text-gray-400">Completed</div>
              <div className="text-blue-600 dark:text-blue-400 font-bold">
                {pomodoro.stats.completedPomodoros}
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">
              <div className="text-gray-500 dark:text-gray-400">Total Time</div>
              <div className="text-green-600 dark:text-green-400 font-bold">
                {Math.floor(pomodoro.stats.totalWorkTime / 60)}m
              </div>
            </div>
          </div>
        </div>

        {/* Team Members Section */}
        <div className="mt-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Team Members
          </h3>
          
          {/* Add new member input */}
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newMemberName}
              onChange={(e) => setNewMemberName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddMember();
                }
              }}
              placeholder="Add team member..."
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md 
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-white 
                       placeholder-gray-500 dark:placeholder-gray-400
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleAddMember}
              disabled={!newMemberName.trim()}
              className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed
                       text-white rounded-md transition-colors duration-200"
              title="Add team member"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Team members list */}
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {teamMembers.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                No team members yet
              </p>
            ) : (
              teamMembers.map((member, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 
                           rounded-md border border-gray-200 dark:border-gray-600"
                >
                  <span className="text-sm text-gray-900 dark:text-white truncate flex-1 mr-2">
                    {member}
                  </span>
                  <button
                    onClick={() => handleRemoveMember(member)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20
                             rounded transition-colors duration-200"
                    title={`Remove ${member}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Sidebar